import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/UI/Breadcrumb/Breadcrumb';
import { ArrowRightIcon, Wallet, User, Headphones, Video, ImageIcon } from 'lucide-react';
import LoadingSpinner from '../../components/UI/LoadingSpinner/LoadingSpinner';
import apiService from '../../services/api';
import './Dashboard.css';

const SUPPORT_QR_FALLBACK = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent('https://work.weixin.qq.com/ct/wcde3d12f7814a242db34db2e2584a12fdc9')}`;

// Welcome Hero Section
const WelcomeHero = ({ userInfo }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return '早上好';
    if (hour < 18) return '下午好';
    return '晚上好';
  };

  const userName = userInfo?.username || userInfo?.phone || '';

  return (
    <div className="welcome-hero">
      <div className="hero-content">
        <div className="hero-text">
          <span className="hero-tag">{getGreeting()}</span>
          <h1 className="hero-title">
            {userName ? `${userName}，` : ''}欢迎回来
          </h1>
          <p className="hero-subtitle">
            今天是 {currentTime.toLocaleDateString('zh-CN', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              weekday: 'long',
            })}
          </p>
        </div>
        <div className="hero-illustration">
          <div className="floating-elements">
            <div className="element element-1" />
            <div className="element element-2" />
            <div className="element element-3" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Stats Cards
const StatsCards = ({ pointsInfo }) => {
  const fmt = (v) => new Intl.NumberFormat('zh-CN').format(Number.isFinite(v) ? v : 0);

  const rawTotal     = Number(pointsInfo?.totalPoints ?? pointsInfo?.TotalPoints ?? 0);
  const rawAvailable = Number(pointsInfo?.availablePoints ?? 0);
  const pct = rawTotal > 0 ? Math.min(100, Math.round((rawAvailable / rawTotal) * 100)) : 0;
  const qrSource = pointsInfo?.supportQRCode || SUPPORT_QR_FALLBACK;

  return (
    <div className="stats-grid">
      {/* 积分卡片 */}
      <div className="stat-card">
        <div className="stat-card-header">
          <div className="stat-icon">
            <Wallet size={20} />
          </div>
          <span className="stat-title">积分余额</span>
        </div>
        <div className="stat-balance-row">
          <span className="stat-value">{fmt(rawAvailable)}</span>
          <span className="stat-separator">/</span>
          <span className="stat-total">{fmt(rawTotal)}</span>
        </div>
        <div className="stat-progress-wrap">
          <div className="stat-progress-bar" style={{ width: `${pct}%` }} />
        </div>
        <p className="stat-progress-label">可用额度 {pct}%</p>
      </div>

      {/* 支持卡片 */}
      <div className="stat-card stat-card--support">
        <img className="support-qr" src={qrSource} alt="技术支持二维码" />
        <p className="support-label">遇到问题？扫码联系技术支持</p>
      </div>
    </div>
  );
};

// Quick Actions
const QuickActions = ({ onNavigate }) => {
  const actions = [
    {
      title: '我的数字人',
      description: '管理和查看您的数字人模型',
      icon: User,
      color: 'purple',
      path: '/my-avatars',
    },
    {
      title: '生成视频',
      description: '使用AI快速生成数字人视频',
      icon: Video,
      color: 'blue',
      path: '/generate-video',
    },
    {
      title: '声音克隆',
      description: '克隆和管理您的专属声音',
      icon: Headphones,
      color: 'green',
      path: '/voice-cloning',
    },
    {
      title: '生成图片',
      description: '使用AI一键生成高质量图片',
      icon: ImageIcon,
      color: 'orange',
      path: '/generate-image',
    },
  ];

  return (
    <div className="quick-actions">
      <h2 className="section-title">快速操作</h2>
      <div className="actions-grid">
        {actions.map((action, index) => (
          <button
            type="button"
            key={index}
            className={`action-card action-card--${action.color}`}
            onClick={() => onNavigate?.(action.path)}
          >
            <div className="action-icon">
              <action.icon size={22} />
            </div>
            <div className="action-content">
              <h3 className="action-title">{action.title}</h3>
              <p className="action-description">{action.description}</p>
            </div>
            <div className="action-arrow">
              <ArrowRightIcon size={16} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// Main Dashboard
const Dashboard = () => {
  const breadcrumbItems = [{ label: '首页', path: null }];
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleNavigate = useCallback((target) => {
    if (!target) return;
    if (typeof target === 'string' && /^(https?:\/\/|mailto:|tel:)/i.test(target)) {
      window.open(target, '_blank', 'noopener');
      return;
    }
    navigate(target);
  }, [navigate]);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getDashboardData();
      if (response?.success) {
        setDashboardData(response.data);
      } else {
        throw new Error(response?.message || '无法获取仪表盘数据');
      }
    } catch (err) {
      setError(err.message || '加载失败');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return (
    <div className="dashboard-page">
      <Breadcrumb items={breadcrumbItems} />

      {loading && (
        <div className="loading-state">
          <LoadingSpinner />
        </div>
      )}

      {!loading && error && (
        <div className="error-state">
          <p>{error}</p>
          <button className="retry-btn" onClick={fetchDashboard}>重试</button>
        </div>
      )}

      {!loading && !error && dashboardData && (
        <>
          <WelcomeHero userInfo={dashboardData.userInfo} />
          <StatsCards pointsInfo={dashboardData.pointsInfo} />
          <QuickActions onNavigate={handleNavigate} />
        </>
      )}
    </div>
  );
};

export default Dashboard;
